import React, { useMemo } from 'react'
import { useSelector } from '../../../../Flux/hooks.ts'
import type { SinkId, SourceId } from '../../../../Flux/Slice/Visualization/VisualizationSliceTypes.js'
import type { State } from '../../../../Flux/Slice/index.js'
import Map from '../../../Common/Map.js'
import type { VisualizationSampleContainer } from '../../SampleGrouping/VisualizationSampleContainer.js'
import { useDispatch } from 'react-redux'
import { visualizationSlice } from 'Flux/Slice/Visualization/VisualizationSlice.ts'
import { calcAbsoluteEffectiveDiversity, calcGiniSimpsonIndex, calcShannonIndex, calcSpeciesRichness } from 'Util/Visualisation/VisMaths.ts'
import { selectCubeContext } from './Selector/CubeContext.ts'
import { selectFilteredCube } from './Selector/SelectFilteredCube.ts'
import { SinkContextRow } from './SinkContextRow.tsx'

type SamplePoint = {
  sample: VisualizationSampleContainer
  magnitude: number
  coords: [number, number] | null
  label: string
}

type MapDisplayOptions = 'magnitude' | 'shannon' | 'richness' | 'AED' | 'gini'

const mapDisplayTransform: Record<MapDisplayOptions, (data: number[]) => number> = {
  magnitude: (data: number[]) => data.reduce((a, b) => a + b, 0),
  shannon: calcShannonIndex,
  richness: calcSpeciesRichness,
  AED: calcAbsoluteEffectiveDiversity,
  gini: calcGiniSimpsonIndex
}

export default function MapSink({ sourceId, sinkId }: { sourceId: SourceId; sinkId: SinkId }) {
  const cubeCtx = useSelector((s: typeof State) => selectCubeContext(s, sourceId))
  const shared = useSelector((s: typeof State) => s.visualization.sources[sourceId]?.shared)
  const sinkConfig = useSelector((s: typeof State) => s.visualization.sources[sourceId]?.sinks[sinkId]?.config)

  const dispatch = useDispatch()
  const displayModel = (sinkConfig.params?.displayModel || 'magnitude') as MapDisplayOptions

  // Shared, memoised filter — reused across every sink on this source (see SelectFilteredCube).
  const filtered = useSelector((s: typeof State) =>
    selectFilteredCube(s, sourceId, {
      selectedPath: shared?.selectedPath ?? [],
      gradeThreshold: shared?.gradeThreshold ?? 0
    })
  )

  const mapObjects = useMemo<SamplePoint[]>(() => {
    if (cubeCtx.status !== 'ready') return []

    // Per-bucket aggregation preserves the legacy behaviour: every sample in the same
    // bucket gets that bucket's aggregated magnitude (the cube is more granular than the
    // legacy backend's bucket-aggregated response, but we collapse it the same way here
    // to keep visual identity).
    const points: SamplePoint[] = []
    for (let i = 0; i < cubeCtx.selectedBuckets.length; i++) {
      const bucket = cubeCtx.selectedBuckets[i]!
      const filteredBucket = filtered[i]
      const magnitude = filteredBucket
        ? mapDisplayTransform[displayModel](filteredBucket.values.map((v) => v.magnitude || 0))
        : 0
      for (const sample of bucket.samples) {
        points.push({
          sample,
          magnitude,
          coords: sample.sampleMetadata?.sampleCoordinates?.coordinates || null,
          label: `${sample.customerSampleName || sample.id}: ${magnitude}`
        })
      }
    }
    return points
  }, [cubeCtx, filtered, displayModel])

  const maxMag = Math.max(...mapObjects.map((o) => o.magnitude), 1)

  const handleMapDisplayChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch(
      visualizationSlice.actions.registerSink({
        sourceId,
        sinkconfig: {
          ...sinkConfig,
          params: {
            ...sinkConfig.params,
            displayModel: e.target.value
          }
        }
      })
    )
  }

  return (
    <div className="h-[550px] w-full flex flex-col">
      <SinkContextRow>
        <label htmlFor={`map-display-${sinkId}`} className="opacity-70">
          Display
        </label>
        <select
          id={`map-display-${sinkId}`}
          className="select select-bordered select-xs w-auto max-w-full"
          value={displayModel}
          onChange={handleMapDisplayChange}
        >
          <option value="magnitude">Magnitude Heatmap</option>
          <option value="shannon">Shannon Index Heatmap</option>
          <option value="richness">Richness Heatmap</option>
          <option value="AED">Absolute Effective Diversity Heatmap</option>
          <option value="gini">Gini-Simpson Index Heatmap</option>
        </select>
      </SinkContextRow>
      <Map
        objects={mapObjects}
        getCoordinates={(o) => o.coords}
        getIntensity={(o) => ((o.magnitude * 2) / maxMag) ** 2} // a exponential intensity scaling to better show differences
        getMarkerLabel={(o) => o.label}
      />
    </div>
  )
}
